import { makeAutoObservable } from "mobx";
import i18n from "../../i18n";
import type { Serializable } from "./Serializable";

export type UserProfilePayload = {
  family_name: string;
  name: string;
  salutation: string;
  country: string;
  locale: string;
  job_title: string;
  industry: string;
  interests: string[];
  role: string;
  company_name: string;
  newsletter: "yes" | "no";
  terms_accepted: "yes" | "no";
  email_verified?: boolean;
  email?: string;
  avatar_url?: string;
  two_factor_auth?: "yes" | "no";
};

export type UserSession = {
  session_id: string;
  timestamp: string;
  source: string;
  current: boolean;
};

export class UserProfile implements Serializable {
  public firstName = "";
  public lastName = "";
  public email = "";
  public country = "";
  public locale = "";
  public jobTitle = "";
  public industry = "";
  public interests: string[] = [];
  public role = "";
  public salutation = "";
  public emailVerified = false;
  public newsletter: "yes" | "no" = "yes";
  public termsAccepted: "yes" | "no" = "no";
  public avatarUrl?: string;
  public sessions: UserSession[] = [];
  public companyName = "";
  public twoFactorAuth: "yes" | "no" = "no";
  public errors: string[] = [];

  constructor(p: Partial<UserProfile>) {
    Object.assign(this, p);
    makeAutoObservable(this);
  }

  public get isProfileFilled(): boolean {
    this.errors = [];
    if (this.salutation === "") {
      this.errors.push(i18n.t("Salutation is required"));
    }
    if (this.firstName.trim().length < 2) {
      this.errors.push(i18n.t("First name must be at least 2 characters"));
    }
    if (this.lastName.trim().length < 2) {
      this.errors.push(i18n.t("Last name must be at least 2 characters"));
    }
    // if (this.jobTitle.trim() === "") {
    //   this.errors.push(i18n.t("Job title is required"));
    // }
    if (this.industry.trim() === "") {
      this.errors.push(i18n.t("Industry is required"));
    }
    if (this.role.trim() === "") {
      this.errors.push(i18n.t("Role is required"));
    }
    // if (this.companyName.trim() === "") {
    //   this.errors.push("Company name is required");
    // }
    if (this.country.trim() === "") {
      this.errors.push(i18n.t("Country is required"));
    }
    if (this.locale.trim() === "") {
      this.errors.push(i18n.t("Language is required"));
    }
    if (this.termsAccepted === "no") {
      this.errors.push(i18n.t("Please accept the terms and conditions"));
    }

    return this.errors.length === 0;
  }

  public get profileErrors(): string[] {
    return this.errors;
  }

  toJSON(): UserProfilePayload {
    return {
      family_name: this.lastName,
      name: this.firstName,
      salutation: this.salutation,
      country: this.country,
      locale: (this.locale || "EN").toUpperCase(),
      job_title: this.jobTitle,
      industry: this.industry,
      interests: this.interests,
      role: this.role,
      company_name: this.companyName,
      newsletter: this.newsletter,
      terms_accepted: this.termsAccepted,
    };
  }

  static fromJSON(p: UserProfilePayload) {
    return new UserProfile({
      firstName: p.name,
      lastName: p.family_name,
      salutation: p.salutation,
      country: p.country,
      locale: (p.locale || "EN").toUpperCase(),
      jobTitle: p.job_title,
      industry: p.industry,
      interests: p.interests,
      role: p.role,
      companyName: p.company_name,
      newsletter: p.newsletter,
      termsAccepted: p.terms_accepted,
      emailVerified: p.email_verified,
      email: p.email,
      avatarUrl: p.avatar_url,
      twoFactorAuth: p.two_factor_auth,
    });
  }
}
